import { Fragment, useCallback, useEffect, useState } from 'react';

import { SearchableVendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomyKeyFromVendorType } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import { getVendorWords } from '@zola-helpers/client/dist/es/marketplace/vendorWords';
import {
  StarterPackCardRequest,
  StarterPackCardView,
  StarterPackView,
  USCityView,
} from '@zola/svc-marketplace-ts-types';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import LinkV2 from '@zola/zola-ui/src/components/LinkV2/LinkV2';
import { SwitchIcon } from '@zola/zola-ui/src/components/SvgIcons/Switch';

import cx from 'classnames';

import { updatedStarterPackCard } from '~/actions/couples/types/VendorStarterPacksActionTypes';
import { StorefrontFavoriteButton } from '~/components/common/favorites/FavoriteButton';
import { useShowPostFavoriteOptIn } from '~/components/common/modals/InvitesFromVendorsOptIn/showPostFavoriteOptIn';
import { useAppDispatch } from '~/reducers';
import { VendorCardView } from '~/types/responseTypes';
import ApiService from '~/util/apiService';
import { StandardLogFn } from '~/util/logger';
import {
  mapVendorCardToProduct,
  TrackingProductLocation,
  TrackingProductType,
  trackProductClicked,
} from '~/util/trackingHelper';
import vendorSearchUrl from '~/util/vendorSearchUrl';

import VendorDetails from './VendorDetails/VendorDetails';

import styles from './vendorStarterPackCard.module.less';

export type VendorStarterPackCardProps = {
  vendor: StarterPackCardView;
  vendorMarketId?: number | null;
  index: number;
  startingPriceCents: number;
  setStartingPriceCents: (staringPrice: number) => void;
  currentCity?: USCityView;
  serviceLevel?: StarterPackView.ServiceLevelEnum;
};

export const VendorStarterPackCard = (props: VendorStarterPackCardProps): JSX.Element => {
  const {
    vendor,
    vendorMarketId,
    serviceLevel,
    currentCity,
    index,
    startingPriceCents,
    setStartingPriceCents,
  } = props;

  const dispatch = useAppDispatch();

  const [vendorDetails, setVendorDetails] = useState(vendor);
  const [previousStartingPrice, setPreviousStartingPrice] = useState(vendor.costContributionCents);
  const [bounce, setBounce] = useState(false);
  const [fade, setFade] = useState(false);

  const [showFavoriteConfirmation, setShowFavoriteConfirmation] = useState(false);

  useEffect(() => {
    setVendorDetails(vendor);
    setPreviousStartingPrice(vendor.costContributionCents);
  }, [vendor]);

  const swapVendor = useCallback(
    (vendorType: StarterPackCardView.VendorTypeEnum | null) => {
      if (vendorType) {
        const request: StarterPackCardRequest = {
          vendorMarketId: vendorMarketId || 1064, // default to NYC,
          weddingBudgetCents: null,
          serviceLevel: serviceLevel || 'DIY',
          vendorType,
        };
        ApiService.post<StarterPackCardView, StarterPackCardRequest>(
          '/web-marketplace-api/v1/starter-packs/fetch-pack-card',
          request
        )
          .then((response) => {
            const startingPriceLessPreviousVendor = startingPriceCents - previousStartingPrice;
            const newStartingPrice =
              startingPriceLessPreviousVendor + response.costContributionCents;
            setPreviousStartingPrice(response.costContributionCents);
            setStartingPriceCents(newStartingPrice);

            setVendorDetails(response);
            setFade(false);
            setBounce(true);
            dispatch(updatedStarterPackCard(response));
          })
          .catch(StandardLogFn);
      }
    },
    [
      serviceLevel,
      vendorMarketId,
      startingPriceCents,
      setStartingPriceCents,
      previousStartingPrice,
      dispatch,
    ]
  );

  useEffect(() => {
    if (showFavoriteConfirmation) {
      const favoriteTimer = setTimeout(() => {
        setShowFavoriteConfirmation(false);
        setTimeout(() => {
          setFade(true);
          setTimeout(() => {
            swapVendor(vendorDetails.vendorType);
          }, 1000);
        }, 500);
      }, 400);
      return () => clearTimeout(favoriteTimer);
    }

    return undefined;
  }, [showFavoriteConfirmation, swapVendor, vendorDetails.vendorType]);

  const showPostFavoriteOptIn = useShowPostFavoriteOptIn();

  if (!vendorDetails) {
    return <Fragment />;
  }

  const vendorTypeKey = vendorDetails.vendorType
    ? getVendorTaxonomyKeyFromVendorType(vendorDetails.vendorType)
    : undefined;

  const vendorWords = getVendorWords(vendorTypeKey);

  const srpHref = vendorSearchUrl(
    vendorDetails.storefront?.taxonomyNode?.key as SearchableVendorTaxonomyKey,
    currentCity?.slug
  );

  return (
    <div
      className={cx(styles.wrapper, { [styles.bounceInCard]: bounce, [styles.fadeOutCard]: fade })}
      data-testid="VendorStarterPackCard"
    >
      <div>
        <LinkV2
          className={styles.hed}
          color="black"
          href={srpHref}
          noUnderline
          data-testid={`hed-link-${vendorWords.singular}`}
        >
          {vendorWords.pill || vendorWords.singular}
        </LinkV2>
        <VendorDetails
          className={styles.cardWrapper}
          vendor={vendorDetails.storefront as VendorCardView}
          position={index + 1}
          location={TrackingProductLocation.VENDOR_STARTER_PACKS}
          onClick={() =>
            trackProductClicked({
              ...mapVendorCardToProduct(vendorDetails.storefront, index + 1),
              productType: TrackingProductType.STOREFRONT_CARD,
              location: TrackingProductLocation.VENDOR_STARTER_PACKS,

              // simpleCard means we aren't showing badges or recommendation counts
              coupon: [],
              recommendationValue: undefined,
            })
          }
          showFavoriteConfirmation={showFavoriteConfirmation}
          costContributionCents={vendorDetails.costContributionCents}
          key={vendorDetails.storefront?.id || `vendor-card-${index}`}
          carouselPhotoIds={vendor.imageIds || []}
        />
      </div>
      <div>
        <div className={styles.ctaWrapper}>
          <ButtonV3
            className={styles.refresh}
            variant="primary"
            onClick={() => {
              setFade(true);
              setTimeout(() => {
                swapVendor(vendorDetails.vendorType);
              }, 400);
            }}
          >
            <SwitchIcon width={18} height={18} alt="Switch vendor" />
          </ButtonV3>
          {vendorDetails.storefront?.storefrontUuid && (
            <StorefrontFavoriteButton
              uuid={vendorDetails.storefront?.storefrontUuid}
              vendorPhotoUuid={vendorDetails.storefront?.storefrontCoverId as string}
              variant="circleZuiV3"
              className={styles.favoriteButton}
              afterFavoriteCallback={() => {
                setShowFavoriteConfirmation(true);
                showPostFavoriteOptIn();
              }}
              location={TrackingProductLocation.VENDOR_STARTER_PACKS}
              buttonClassOverrides={styles.buttonOverrides}
              suppressToast
            />
          )}
        </div>
        {vendorDetails.vendorType && (
          <div className={styles.seeMoreVendorsWrapper}>
            <LinkV2 color="black" href={srpHref}>
              More&nbsp;
              <span className={styles.vendorWord}>{vendorWords.pill || vendorWords.plural}</span>
            </LinkV2>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorStarterPackCard;
